import { ChangeDetectionStrategy, Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BreadcrumbNavigationService, ConfigurationService, CoursesService, PathsService, PortalConfigurationService, UrlService } from '@draeger-academy/common';
import { CartService } from '@draeger-academy/common/lib/services/cart/cart.service';
import { GenericModalService } from '@draeger-academy/presentation/lib/modules/generic-modal';
import { LocalizeRouterService } from '@gilsdav/ngx-translate-router';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CartItems } from '../../../../../../models/cartItems';

@Component({
    selector: 'da-event-detail',
    templateUrl: './event-detail.component.html',
    styleUrls: ['./event-detail.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class EventDetailComponent implements OnInit {
    public durationDays: number;
    public durationTime: number;
    public fromCoursesPage = false;
    public currency = '';
    public envApi = environment.APIEndpoint;
    public currencyIsTheSameLikeOtherCourses: boolean;
    public courseTitle: string;
    private _$event: BehaviorSubject<CartItems> = new BehaviorSubject<CartItems>(null);
    private _subscription: Subscription;
    private _modalId: string;
    @ViewChild('warn', { static: true }) private _warn;
    @ViewChild('notTheSameCurrencyModal', { static: true }) private _notTheSameCurrencyModal;
    private _$showCartUpdate: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private _$currencyPositionBefore: BehaviorSubject<string> = new BehaviorSubject('');

    constructor(
        private route: ActivatedRoute,
        private _coursesService: CoursesService,
        private _breadcrumbNavigationService: BreadcrumbNavigationService,
        private _configurationService: ConfigurationService,
        private _cartService: CartService,
        private _localizeRouterService: LocalizeRouterService,
        private _router: Router,
        private _urlService: UrlService,
        private _genericModalService: GenericModalService,
        private _translateService: TranslateService,
        private _pathsService: PathsService,
        private _portalConfigurationService: PortalConfigurationService
    ) {}

    public get event$(): Observable<CartItems> {
        return this._$event.asObservable();
    }

    public get showCartUpdate$(): Observable<boolean> {
        return this._$showCartUpdate.asObservable();
    }

    public get currencyPositionBefore$(): Observable<string> {
        return this._$currencyPositionBefore.asObservable();
    }

    private _$trainers: BehaviorSubject<any> = new BehaviorSubject([]);
    public get trainers$(): Observable<any> {
        return this._$trainers.asObservable();
    }

    private _$showTrainers: BehaviorSubject<boolean> = new BehaviorSubject(false);
    public get showTrainers$(): Observable<boolean> {
        return this._$showTrainers.asObservable();
    }

    private _$dateFormat: BehaviorSubject<string> = new BehaviorSubject('');
    public get dateFormat$(): Observable<string> {
        return this._$dateFormat.asObservable();
    }

    private _$pricePattern: BehaviorSubject<string> = new BehaviorSubject('');
    public get pricePattern$(): Observable<string> {
        return this._$pricePattern.asObservable();
    }
    private _$formattedPrice: BehaviorSubject<string> = new BehaviorSubject('');
    public get formattedPrice$(): Observable<string> {
        return this._$formattedPrice.asObservable();
    }

    ngOnDestroy() {
        this._subscription.unsubscribe();
    }

    ngOnInit() {
        this.getEventDetails();
        if (this._configurationService.hasFeature('EventTrainers')) {
            this._$showTrainers.next(true);
        }

        if (this._configurationService.isPartnerPage()) {
            this._configurationService.currentPortal$.subscribe((portalId) => {
                if (portalId) {
                this._portalConfigurationService.loadPortalPreferences(portalId).then((preferences) => {
                    const _dateFormat = this._portalConfigurationService.getPortalDateFormat(preferences);
                    this._$dateFormat.next(_dateFormat);
                });
            }
        });
        } else {
            this._$dateFormat.next('dd.MM.yyyy');
        }
    }

    getEventDetails() {
        this._subscription = this.route.params.subscribe((params) => {
            const _event = params['id'];
            this.fromCoursesPage = params['courses'] === 'true' ? true : false;

            this._coursesService.getEventDetails(_event).then(
                (data) => {
                    if (data) {
                        this._$event.next(data);
                        if (data) {
                            this._breadcrumbNavigationService.setEventBreadcrumb(data, 2);
                        }
                        const d1 = new Date(data.endDateTime);
                        const d2 = new Date(data.beginDateTime);
                        this.durationTime = this.hoursDiff(d2, d1);
                        this.durationDays = this.daysDiff(d2, d1);
                        this._$trainers.next(data.trainers);
                        this.currency = data.currency;
                        this.formatingPrice(data.coursePrice);
                        if (data.courseUnicodeTitle && data.courseUnicodeTitle !== null && data.courseUnicodeTitle !== '') {
                            this.courseTitle = data.courseUnicodeTitle;
                        } else {
                            this.courseTitle = data.courseName;
                        }
                    }
                },
                (error) => {
                    console.log(error);
                }
            );
        });
    }

    public secondsDiff(d1, d2) {
        const millisecondDiff = d2 - d1;
        const secDiff = Math.floor((d2 - d1) / 1000);
        return secDiff;
    }

    public minutesDiff(d1, d2) {
        const seconds = this.secondsDiff(d1, d2);
        const minutesDiff = Math.floor(seconds / 60);
        return minutesDiff;
    }

    public hoursDiff(d1, d2) {
        const minutes = this.minutesDiff(d1, d2);
        const hoursDiff = Math.floor(minutes / 60);
        return hoursDiff;
    }

    public daysDiff(d1, d2) {
        const hours = this.hoursDiff(d1, d2);
        const daysDiff = Math.ceil(hours / 24);
        return daysDiff;
    }

    public addToCart(eventId: string) {
        let _site = this._configurationService.getCurrentSite();
        if (this._configurationService.isPartnerPage()) {
            this._configurationService.currentPortal$.subscribe((portalId) => {
                if (portalId) {
                    _site = portalId;
                }
            });
        }

        this.currencyIsTheSameLikeOtherCourses = this._configurationService.checkIfCurrencyIsDifferentToCartItemsCurrency(
            _site,
            this.currency
        );
        if (!this.currencyIsTheSameLikeOtherCourses) {
            this._overlayNotTheSameCurrency();
        } else {
            if (localStorage.getItem(`cartItems${_site}`)) {
                const cartItems = JSON.parse(this._urlService.decode(localStorage.getItem(`cartItems${_site}`)));

                if (cartItems && cartItems.events.filter((courseItem) => courseItem === eventId).length) {
                    this._overlayWarnHandler();
                } else {
                    this._cartService.addEventToCart(eventId);
                    this._overlayNotification();
                }
            } else {
                this._cartService.addEventToCart(eventId);
                this._overlayNotification();
            }
        }
    }

    public closeModal() {
        if (this._modalId) {
            this._genericModalService.close(this._modalId);
        }
    }

    public goToCart() {
        this.closeModal();
        this._$showCartUpdate.next(false);
        this._cartService.goToCart();
    }

    public goBack(id) {
        const _detailPath = this._pathsService.getCourseDetailLink(id);
        this._router.navigate([_detailPath], { queryParams: { dates: true } });
    }

    private _overlayWarnHandler(): void {
        this._modalId = this._genericModalService.create(this._warn);
        this._genericModalService.open(this._modalId, this._warn, true);
    }

    private async _overlayNotification() {
        this._$showCartUpdate.next(true);
        setTimeout(() => this._$showCartUpdate.next(false), 4000);
    }

    private _overlayNotTheSameCurrency(): void {
        this._modalId = this._genericModalService.create(this._notTheSameCurrencyModal);
        this._genericModalService.open(this._modalId, this._notTheSameCurrencyModal, true);
    }

    private formatingPrice(event){      
        if (this._configurationService.isPartnerPage()) {
                this._portalConfigurationService.currencyPattern$.subscribe((currencyPattern) => {
                    this._$formattedPrice.next(this.formatPrice(event.coursePrice, currencyPattern, this.currency));
                });
        } else {
            this._$pricePattern.next('#.##0,00 " $"');
            this._$formattedPrice.next(this.formatPrice(event.coursePrice, this._$pricePattern.getValue(), this.currency));
        }
    }



    private formatPrice(price: any, formatPattern, currency){
        if (formatPattern){
            return this._configurationService.formatPrice(price, formatPattern, currency );
        } else {
            return price;
        }
    }
}
